import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
// Mock data - replace with actual user data
const USER_PATTERN = 'You tend to take responsibility early and push through resistance.';
// Mock timeline entries with different types
const ENTRIES = [{
  id: 1,
  date: 'Mon, Jan 15',
  type: 'note',
  userNote: 'Had a tense review meeting with the product team. I pushed hard for shipping the feature early. They pushed back about quality concerns and the room went quiet.',
  mirrorReflection: 'This repeats your pattern of pushing for resolution under disagreement. You optimized for speed over alignment.'
}, {
  id: 2,
  date: 'Fri, Jan 12',
  type: 'situation',
  situationData: {
    situationType: 'Conflict',
    role: 'Driving it forward',
    tension: 'Speed vs quality',
    outcome: 'Still unresolved',
    context: 'Weekly review with product team'
  },
  mirrorReflection: 'This fits your tendency to step in and push for resolution when a situation remains ambiguous. The tension between speed and alignment shows up repeatedly in how you operate, especially when responsibility is not clearly held. The situation remaining unresolved keeps the same loop active rather than closing it.'
}, {
  id: 3,
  date: 'Thu, Jan 11',
  type: 'mode',
  modeData: 'Pushing, Carrying',
  mirrorReflection: 'This repeats your pattern of prioritizing momentum over alignment. You were in execution mode, not collaborative mode.'
}];
// User state
const HAS_ENTRIES = ENTRIES.length > 0;
export function WorkMirrorAppHome() {
  const navigate = useNavigate();
  const handleNoteEntry = () => {
    navigate('/work-mirror/first-entry');
  };
  const handleSituationDecode = () => {
    navigate('/work-mirror/decode-situation');
  };
  const handleModeLog = () => {
    navigate('/work-mirror/log-mode');
  };
  return <>
      <Helmet data-id="element-4104">
        <title data-id="element-4105">Your Work Mirror | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4106" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4107">
        {/* Minimal header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2B2B2B]" data-id="element-4108">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4109">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-4110">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs" className="h-6 w-auto" width="24" height="24" data-id="element-4111" />
              <span className="text-sm font-mono text-gray-500" data-id="element-4112">
                Work Mirror
              </span>
            </Link>

            <button className="text-sm text-gray-600 hover:text-gray-400 transition-colors" data-id="element-4113">
              Settings
            </button>
          </div>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-4114">
          <div className="max-w-3xl mx-auto space-y-16" data-id="element-4115">
            {/* Pattern Plaque (Invariant) */}
            <section className="space-y-6 pt-12" data-id="element-4116">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-widest" data-id="element-4117">
                Your pattern
              </p>

              <p className="text-xl md:text-2xl text-[#F5F5F5] leading-relaxed font-light" data-id="element-4118">
                {USER_PATTERN}
              </p>
            </section>

            {/* Primary Action Area - Three Input Surfaces */}
            <section className="pt-8 space-y-8" data-id="element-4119">
              <h2 className="text-base font-medium text-[#F5F5F5]" data-id="element-4120">
                Add to your mirror
              </h2>

              <div className="space-y-4" data-id="element-4121">
                {/* 1. Note what happened */}
                <button onClick={handleNoteEntry} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] transition-colors bg-[#0A0A0A]" data-id="element-4122">
                  <div className="space-y-2" data-id="element-4123">
                    <h3 className="text-base font-medium text-[#F5F5F5]" data-id="element-4124">
                      Note what happened
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4125">
                      Write a few lines about something from today.
                    </p>
                  </div>
                </button>

                {/* 2. Decode a situation */}
                <button onClick={handleSituationDecode} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] transition-colors bg-[#0A0A0A]" data-id="element-4126">
                  <div className="space-y-2" data-id="element-4127">
                    <h3 className="text-base font-medium text-[#F5F5F5]" data-id="element-4128">
                      Decode a situation
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4129">
                      Log the shape of a specific situation in 30 seconds.
                    </p>
                  </div>
                </button>

                {/* 3. Log today's mode */}
                <button onClick={handleModeLog} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] transition-colors bg-[#0A0A0A]" data-id="element-4130">
                  <div className="space-y-2" data-id="element-4131">
                    <h3 className="text-base font-medium text-[#F5F5F5]" data-id="element-4132">
                      Log today's mode
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4133">
                      Just mark what mode you were mostly in today.
                    </p>
                  </div>
                </button>
              </div>
            </section>

            {/* Timeline / History */}
            <section className="space-y-12 pt-12 border-t border-[#2B2B2B]" data-id="element-4134">
              {!HAS_ENTRIES ?
            // Empty state
            <div className="py-16" data-id="element-4135">
                  <p className="text-base text-gray-600 leading-relaxed" data-id="element-4136">
                    Your mirror will start filling up as you use it.
                  </p>
                </div> :
            // Timeline with entries
            <div className="space-y-12" data-id="element-4137">
                  {ENTRIES.map(entry => <div key={entry.id} className="space-y-6" data-id="element-4138">
                      {/* Date */}
                      <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-4139">
                        {entry.date}
                      </p>

                      {/* Entry content based on type */}
                      {entry.type === 'note' && <>
                          <div className="space-y-3" data-id="element-4140">
                            <p className="text-sm text-gray-600" data-id="element-4141">You wrote:</p>
                            <p className="text-base text-gray-400 leading-relaxed" data-id="element-4142">
                              {entry.userNote}
                            </p>
                          </div>

                          <div className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-4143">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-4144">Mirror</p>
                            <p className="text-base text-[#F5F5F5] leading-relaxed" data-id="element-4145">
                              {entry.mirrorReflection}
                            </p>
                          </div>
                        </>}

                      {entry.type === 'situation' && <>
                          {/* Part 1 — The Recorded Situation */}
                          <div className="space-y-4" data-id="element-4146">
                            <p className="text-sm text-gray-600" data-id="element-4147">Situation</p>

                            <div className="space-y-2 text-sm" data-id="element-4148">
                              <p className="text-gray-500" data-id="element-4149">
                                <span className="text-gray-600" data-id="element-4150">Type: </span>
                                <span className="text-gray-400" data-id="element-4151">
                                  {entry.situationData.situationType}
                                </span>
                              </p>
                              <p className="text-gray-500" data-id="element-4152">
                                <span className="text-gray-600" data-id="element-4153">Role: </span>
                                <span className="text-gray-400" data-id="element-4154">
                                  {entry.situationData.role}
                                </span>
                              </p>
                              <p className="text-gray-500" data-id="element-4155">
                                <span className="text-gray-600" data-id="element-4156">Tension: </span>
                                <span className="text-gray-400" data-id="element-4157">
                                  {entry.situationData.tension}
                                </span>
                              </p>
                              <p className="text-gray-500" data-id="element-4158">
                                <span className="text-gray-600" data-id="element-4159">Outcome: </span>
                                <span className="text-gray-400" data-id="element-4160">
                                  {entry.situationData.outcome}
                                </span>
                              </p>

                              {/* Optional context */}
                              {entry.situationData.context && <p className="text-xs text-gray-600 pt-2" data-id="element-4161">
                                  {entry.situationData.context}
                                </p>}
                            </div>
                          </div>

                          {/* Part 2 — The Mirror Annotation */}
                          <div className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-4162">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-4163">Mirror</p>
                            <div className="space-y-4 text-base text-[#F5F5F5] leading-relaxed" data-id="element-4164">
                              {entry.mirrorReflection.split('\n\n').map((paragraph, i) => <p key={i} data-id="element-4165">{paragraph}</p>)}
                            </div>
                          </div>
                        </>}

                      {entry.type === 'mode' && <>
                          <div className="space-y-3" data-id="element-4166">
                            <p className="text-sm text-gray-600" data-id="element-4167">
                              You logged your mode:
                            </p>
                            <p className="text-xl text-gray-400 font-light" data-id="element-4168">
                              {entry.modeData}
                            </p>
                          </div>

                          <div className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-4169">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-4170">Mirror</p>
                            <p className="text-base text-[#F5F5F5] leading-relaxed" data-id="element-4171">
                              {entry.mirrorReflection}
                            </p>
                          </div>
                        </>}
                    </div>)}
                </div>}
            </section>
          </div>
        </main>
      </div>
    </>;
}